import { useEffect, useMemo, useState } from 'react';

import { useLazyQuery } from '@apollo/client';
import { Dialog, DialogContent, Grid } from '@mui/material';

import { navigate } from '@redwoodjs/router';
import { MetaTags, useMutation } from '@redwoodjs/web';

import Loader from 'src/components/Loader/Loader';
import TableList from 'src/components/TableList/TableList';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import StatusChip from 'src/components/UserStatusChip/UserStatusChip';
import { Hierarchy, USER_MANAGEMENT_TABLE_HEADERS } from 'src/constants';
import { DELETE_USER_MUTATION } from 'src/graphql/mutations/deleteUser.mutation';
import { GET_USERS } from 'src/graphql/queries/users.query';
import { PageContent } from 'src/layouts/MainLayout/PageContent';
import { enumToCamelCase } from 'src/lib/formatters';
import {
    FilterOptionsType,
    updateTableFilters,
    usersTableFilteredData,
} from 'src/utils/tableUtils';
import { onError } from 'src/utils/utils';

import ModalContent from '../../components/ModalContent/ModalContent';

const UserManagementPage = () => {
    const [tableFilters, setTableFilters] = useState({});
    const [usersData, setUsersData] = useState([]);

    // GraphQL query to fetch list of users
    const [getUsers, { loading, refetch }] = useLazyQuery(GET_USERS, {
        fetchPolicy: 'cache-and-network',
        onError: (error) => onError(error),
        onCompleted: ({ users }) => {
            setUsersData(users);
        },
    });

    useEffect(() => {
        const fetchUsers = async () => {
            await getUsers();
        };
        fetchUsers();
    }, [getUsers]);

    // GraphQL query to delete a user
    const [deleteUser] = useMutation(DELETE_USER_MUTATION, {
        onCompleted: () => {
            showSuccessToast('User Deleted Successfully');
            refetch();
        },
        onError: (error) => {
            showErrorToast(error.message ?? 'Error Deleting the User');
        },
    });

    // load specified user's record into state when attempting to delete a user
    const [userId, setUserId] = useState<number | null>();

    // load table data to be passed to reusable table component

    const tableData = useMemo(() => {
        return usersTableFilteredData({ users: usersData }, tableFilters).map((user) => {
            return {
                ...user,
                statusChip: <StatusChip type="user" status={user.status} />,
            };
        });
    }, [usersData, tableFilters]);

    const filterOptions = () => {
        const users = usersData ?? [];
        return Object.fromEntries([
            [
                'User Role',
                Array.from(new Set(users.map(({ role }) => role?.name))).map((role) => ({
                    value: role,
                    label: role,
                })),
            ],
            [
                'Hierarchy Level',
                Object.keys(Hierarchy).map((key) => ({
                    value: Hierarchy[`${key}`],
                    label: Hierarchy[`${key}`].replaceAll('_', ' '),
                })),
            ],
        ]) as FilterOptionsType;
    };

    const exportData = useMemo(() => {
        if (usersData?.length > 0) {
            return usersData.map((user) => {
                const status = user.status;
                const partner =
                    !user.business?.hierarchyLevel ||
                    user.business?.hierarchyLevel === Hierarchy.NPI
                        ? '-'
                        : user.business?.legalName;
                return {
                    ...user,
                    role: enumToCamelCase(user.role.name),
                    roleId: user.role.id,
                    partner,
                    statusChip: status,
                    hierarchyLevel: enumToCamelCase(user.role.hierarchyLevel),
                };
            });
        }

        return [];
    }, [usersData]);

    // Navigate to new page to handle the creation of a new user
    const addNewUser = () => {
        navigate('/user-management/add-user');
    };

    // Navigate to view user by id page
    const handleViewUser = ({ id }: { id: number }) => {
        navigate(`/user-management/users/${id}`);
    };

    // track the open/close state of the delete modal
    const [openDeleteModal, setOpenDeleteModal] = useState(false);

    // Delete record callback sent to table component
    const handleOpenDeleteModal = (recordId) => {
        setUserId(recordId);
        setOpenDeleteModal(true);
    };

    // Close the delete modal
    const handleCloseDeleteModal = () => {
        setOpenDeleteModal(false);
    };

    // Function used to delete the specified record
    const handleDeleteRecord = () => {
        // GraphQL mutation
        deleteUser({ variables: { id: userId } });
        handleCloseDeleteModal();
    };

    // TO-DO NOTE: move reused styles to theme config
    return (
        <PageContent heading="User Management" subHeading="View and manage Buddy Users">
            <MetaTags title="UserManagement" description="UserManagement page" />
            <Grid container sx={{ gap: '24px' }}>
                <Grid item xs={12}>
                    {/* Display loader if data is still fetching */}
                    {loading || !tableData ? (
                        <Loader />
                    ) : (
                        <TableList
                            addRecordButtonText="Add New User"
                            data={tableData}
                            exportData={exportData}
                            headers={USER_MANAGEMENT_TABLE_HEADERS}
                            title="User List"
                            addRecordCallBack={addNewUser}
                            handleViewRecord={handleViewUser}
                            deleteRecordCallBack={handleOpenDeleteModal}
                            enableFilter
                            enableExport
                            filterOptions={filterOptions()}
                            updateTableFilters={(filter: string, newFilterValue: string) =>
                                setTableFilters((prevFilters) =>
                                    updateTableFilters(prevFilters, newFilterValue, filter)
                                )
                            }
                        />
                    )}
                </Grid>
            </Grid>
            <Dialog
                open={openDeleteModal}
                onClose={handleCloseDeleteModal}
                aria-labelledby="delete-user-dialog-title"
                sx={{
                    '& .MuiDialog-paper': {
                        width: '400px',
                    },
                }}
            >
                <DialogContent>
                    <ModalContent
                        handleClose={handleCloseDeleteModal}
                        handleAction={handleDeleteRecord}
                        header="Delete User?"
                        message="Are you sure you want to permanently delete this user? The user cannot be restored once deleted"
                        buttonText="Delete"
                    />
                </DialogContent>
            </Dialog>
        </PageContent>
    );
};

export default UserManagementPage;
